// Подтверждение действия, которое нельзя отменить или которое затрагивает других
// (components/ConfirmDialog/README.md; ux.md, раздел 1): без крестика, иконка в квадрате,
// вопрос с объектом, последствия числами, «Отмена» в фокусе и кнопка «глагол + объект».
import * as Dialog from '@radix-ui/react-dialog';
import { Archive, Trash2 } from 'lucide-react';
import { useRef, useState } from 'react';
import type { ReactNode, RefObject } from 'react';
import { cx } from '../lib/cx.ts';
import { Icon } from '../lib/icon.tsx';
import { relayEscape } from '../lib/layers.ts';
import { useReturnFocus } from '../lib/return-focus.ts';
import { ToastLayerRegion } from '../lib/toast-layer.tsx';
import type { LucideIcon } from '../lib/icon.tsx';
import { Button } from './Button.tsx';

/** danger — удаление и необратимое (красная кнопка); warn — архив, отзыв доступа (главная кнопка). */
export type ConfirmTone = 'danger' | 'warn';

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Вопрос с объектом: «Перенести в архив: Морозов О. С.?». */
  title: ReactNode;
  /** Последствия числами и что не пострадает. */
  description?: ReactNode;
  /** «Глагол + объект»: «Перенести в архив». Никогда «ОК». */
  confirmLabel: string;
  /** Выполняет действие; пока промис не завершён — спиннер, при ошибке окно остаётся. */
  onConfirm: () => void | Promise<unknown>;
  tone?: ConfirmTone;
  icon?: LucideIcon;
  /** Кнопка недоступна, пока не введено название (самое разрушительное действие). */
  confirmDisabled?: boolean;
  /** Поле «Введите название…» и другое содержимое под текстом. */
  children?: ReactNode;
  /** Куда вернуть фокус после закрытия, если окно открыли из меню; иначе — откуда открыли. */
  returnFocusRef?: RefObject<HTMLElement | null>;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  onConfirm,
  tone = 'danger',
  icon,
  confirmDisabled = false,
  children,
  returnFocusRef,
}: ConfirmDialogProps) {
  const cancelRef = useRef<HTMLButtonElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [pending, setPending] = useState(false);
  const returnFocus = useReturnFocus(open, contentRef, { returnFocusRef });

  const confirm = async () => {
    setPending(true);
    try {
      await onConfirm();
      onOpenChange(false);
    } catch {
      // Ошибку показывает вызывающий (тост с «Повторить»); окно остаётся открытым.
    } finally {
      setPending(false);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={(next) => (pending ? undefined : onOpenChange(next))}>
      <Dialog.Portal>
        <Dialog.Overlay className="kp-overlay">
          <Dialog.Content
            ref={contentRef}
            role="alertdialog"
            className="t-modal kp-modal-sm"
            onOpenAutoFocus={(event) => {
              // Поле подтверждения вводом названия получает фокус первым, иначе — «Отмена»:
              // выбор радио и флажками в окне фокус у «Отмена» не забирает.
              const field = contentRef.current?.querySelector<HTMLElement>(
                'input:not([type="radio"]):not([type="checkbox"]), textarea',
              );
              event.preventDefault();
              (field ?? cancelRef.current)?.focus();
            }}
            onCloseAutoFocus={returnFocus}
            // Esc, пока окно уходит, — следующему слою (044, MOT-07).
            onEscapeKeyDown={(event) => {
              if (open) return;
              event.preventDefault();
              relayEscape(contentRef.current, event);
            }}
            // Клик мимо окна подтверждения не закрывает его: ответ — только кнопкой или Esc.
            onPointerDownOutside={(event) => event.preventDefault()}
            {...(description == null ? { 'aria-describedby': undefined } : {})}
          >
            <div className="t-modal-h">
              <span
                className={cx('t-confirm-ico', tone === 'warn' && 'kp-warn')}
                aria-hidden="true"
              >
                <Icon icon={icon ?? (tone === 'warn' ? Archive : Trash2)} />
              </span>
              <div className="kp-grow">
                <Dialog.Title asChild>
                  <h2>{title}</h2>
                </Dialog.Title>
                {description != null ? (
                  <Dialog.Description asChild>
                    <p>{description}</p>
                  </Dialog.Description>
                ) : null}
              </div>
            </div>
            {children != null ? <div className="t-modal-b">{children}</div> : null}
            <div className="t-modal-f kp-confirm-f">
              <span className="sp" />
              <Dialog.Close asChild>
                <Button ref={cancelRef} variant="secondary" disabled={pending}>
                  Отмена
                </Button>
              </Dialog.Close>
              <Button
                variant={tone === 'danger' ? 'danger' : 'primary'}
                loading={pending}
                disabled={confirmDisabled}
                onClick={() => void confirm()}
              >
                {confirmLabel}
              </Button>
            </div>
            <ToastLayerRegion active={open} />
          </Dialog.Content>
        </Dialog.Overlay>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
